// Query keys for TanStack Query cache management
// These are used by query hooks and mutation hooks for cache invalidation

export const PF_QUERY_KEYS = {
  // Activity queries
  activities: 'pf-activities',
  activity: 'pf-activity',
  activitiesByItemId: 'pf-activities-by-item-id',
  purchaseActivitiesByItemIdSlim: 'pf-purchase-activities-by-item-id-slim',
  purchaseActivitiesByItemIdSlimHeader: 'pf-purchase-activities-by-item-id-slim-header',
  purchaseActivity: 'pf-purchase-activity',

  // Contract queries
  contract: 'pf-contract',
  contractSigningStatus: 'pf-contract-signing-status',
  contractTemplates: 'pf-contract-templates',
  contractValidation: 'pf-contract-validation',

  // Task queries
  task: 'pf-task',
  tasks: 'pf-tasks',
  taskEntry: 'pf-task-entry',
  taskEntryByReference: 'pf-task-entry-by-reference',
  externalTaskEntry: 'pf-external-task-entry',
  dynamicTask: 'pf-dynamic-task',

  // Process queries
  process: 'pf-process',
  processes: 'pf-processes',

  // Form queries
  form: 'pf-form',
  formSigningStatus: 'pf-form-signing-status',
  formValidation: 'pf-form-validation',

  // Operation queries
  operation: 'pf-operation',
  operationRoleAssignments: 'pf-operation-role-assignments',
  operationRoles: 'pf-operation-roles',
  operationTargetInputValues: 'pf-operation-target-input-values',
  operationsAssignees: 'pf-operations-assignees',

  // Role queries
  roles: 'pf-roles',
  roleAssignments: 'pf-role-assignments',

  // Scrive queries
  scriveConnection: 'pf-scrive-connection',
  scriveConnections: 'pf-scrive-connections',

  // Station queries
  pagedStations: 'pf-paged-stations',
  pagedStationsStats: 'pf-paged-stations-stats',

  // Work order queries
  pagedWorkOrdersStats: 'pf-paged-work-orders-stats',
  externalPagedWorkOrders: 'pf-external-paged-work-orders',
  externalPagedWorkOrdersStats: 'pf-external-paged-work-orders-stats',
  workOperationItem: 'pf-work-operation-item',
  workOperationItemStatus: 'pf-work-operation-item-status',
  workOperationItemState: 'pf-work-operation-item-state',
  workOrderPlanFromNodeId: 'pf-work-order-plan-from-node-id',

  // Calculation queries
  purchaseCalculation: 'pf-purchase-calculation',
  purchaseCalculationSync: 'pf-purchase-calculation-sync',
  recommendedOperations: 'pf-recommended-operations',

  // Other queries
  executors: 'pf-executors',
  integrations: 'pf-integrations',
  observationCategories: 'pf-observation-categories',
  stakeholdersForItem: 'pf-stakeholders-for-item',
  templates: 'pf-templates',
} as const;

export type PfQueryKey = (typeof PF_QUERY_KEYS)[keyof typeof PF_QUERY_KEYS];

// Map Apollo GraphQL field names to TanStack Query keys
// Used for backward compatibility during migration
const FIELD_NAME_TO_QUERY_KEY: Record<string, PfQueryKey | PfQueryKey[]> = {
  activities: PF_QUERY_KEYS.activities,
  activity: PF_QUERY_KEYS.activity,
  activitiesByItemId: PF_QUERY_KEYS.activitiesByItemId,
  purchaseActivitiesByItemIdSlim: PF_QUERY_KEYS.purchaseActivitiesByItemIdSlim,
  purchaseActivity: PF_QUERY_KEYS.purchaseActivity,
  contract: PF_QUERY_KEYS.contract,
  contractSigningStatus: PF_QUERY_KEYS.contractSigningStatus,
  contractTemplates: PF_QUERY_KEYS.contractTemplates,
  contractValidation: PF_QUERY_KEYS.contractValidation,
  task: PF_QUERY_KEYS.task,
  tasks: PF_QUERY_KEYS.tasks,
  taskEntry: PF_QUERY_KEYS.taskEntry,
  dynamicTask: PF_QUERY_KEYS.dynamicTask,
  process: PF_QUERY_KEYS.process,
  processes: PF_QUERY_KEYS.processes,
  form: PF_QUERY_KEYS.form,
  formSigningStatus: PF_QUERY_KEYS.formSigningStatus,
  formValidation: PF_QUERY_KEYS.formValidation,
  operation: PF_QUERY_KEYS.operation,
  operationRoleAssignments: PF_QUERY_KEYS.operationRoleAssignments,
  operationRoles: PF_QUERY_KEYS.operationRoles,
  roles: PF_QUERY_KEYS.roles,
  roleAssignments: PF_QUERY_KEYS.roleAssignments,
  scriveConnection: PF_QUERY_KEYS.scriveConnection,
  scriveConnections: PF_QUERY_KEYS.scriveConnections,
  executors: PF_QUERY_KEYS.executors,
  integrations: PF_QUERY_KEYS.integrations,
  observationCategories: PF_QUERY_KEYS.observationCategories,
  stakeholders: PF_QUERY_KEYS.stakeholdersForItem,
  templates: PF_QUERY_KEYS.templates,
  purchaseCalculation: PF_QUERY_KEYS.purchaseCalculation,
  purchaseCalculationSync: PF_QUERY_KEYS.purchaseCalculationSync,
  recommendedOperations: PF_QUERY_KEYS.recommendedOperations,
};

export const getQueryKeysForFieldName = (fieldName: string): PfQueryKey[] => {
  const queryKey = FIELD_NAME_TO_QUERY_KEY[fieldName];
  if (!queryKey) {
    return [];
  }
  return Array.isArray(queryKey) ? queryKey : [queryKey];
};
